import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { AuthError, forgotPassword, resetPassword, signIn } from '../lib/auth';
import { activeMembership, useSession } from '../lib/store';
import { goToMembershipHome, homeMembership } from '../lib/config';
import { Card, Notice, Spinner } from '../components/Shell';
import { TextField } from '../components/Field';

type Mode = 'sign-in' | 'forgot' | 'reset';

export function SignIn() {
  const navigate = useNavigate();
  const location = useLocation();
  const load = useSession((s) => s.load);

  const [mode, setMode] = useState<Mode>('sign-in');
  // An invitation link knows the address it was sent to, and that is the only
  // account the invitation will open for. Prefilling it saves a typo that
  // would otherwise surface as the invitation mysteriously not being found.
  const [email, setEmail] = useState((location.state as { email?: string } | null)?.email ?? '');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Where the guard turned them away from, if that is how they got here. There
  // is no default: which screen is home depends on the role they turn out to
  // hold, which is not known until they are signed in.
  const returnTo = (location.state as { from?: string } | null)?.from ?? null;

  // Arriving at a log-in form while already holding an account: a stale tab, a
  // bookmark, or a link that should not have been offered. Send them where
  // signing in would have. Read once, at mount, so that signing in on this very
  // form goes through the handler below and keeps its return path rather than
  // being overtaken by this.
  const wasSignedIn = useRef(useSession.getState().user !== null);
  useEffect(() => {
    if (!wasSignedIn.current) return;
    const session = useSession.getState();
    const home = homeMembership(session.memberships);
    if (home) goToMembershipHome(home);
    else navigate('/', { replace: true });
  }, [navigate]);

  const handle = async (action: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await action();
    } catch (err) {
      setError(err instanceof AuthError ? err.message : 'Something went wrong. Try again.');
    } finally {
      setBusy(false);
    }
  };

  const onSignIn = (event: React.FormEvent) => {
    event.preventDefault();
    void handle(async () => {
      await signIn(email, password);
      await load();

      // On the platform's own host no tenant resolves, so `/portal` and
      // `/admin` both bounce back to the marketing page and it reads as the
      // sign-in having silently failed. Send them to their business instead.
      const session = useSession.getState();
      if (!session.tenant) {
        const home = homeMembership(session.memberships);
        if (home) {
          goToMembershipHome(home);
          return;
        }
      }

      // On a business's own host the tenant resolves, so the only question left
      // is which side of it this person belongs on. The portal is a client's
      // own pets and bookings; landing a sitter there reads as having been
      // signed in as the wrong person.
      const membership = activeMembership(session);
      const home = membership && membership.role !== 'client' ? '/admin' : '/portal';
      navigate(returnTo ?? home, { replace: true });
    });
  };

  const onForgot = (event: React.FormEvent) => {
    event.preventDefault();
    void handle(async () => {
      await forgotPassword(email);
      setNotice(`We sent a reset code to ${email}.`);
      setMode('reset');
    });
  };

  const onReset = (event: React.FormEvent) => {
    event.preventDefault();
    void handle(async () => {
      await resetPassword(email, code, password);
      setNotice('Password changed. Sign in with it now.');
      setMode('sign-in');
      setPassword('');
      setCode('');
    });
  };

  if (wasSignedIn.current) {
    return (
      <div className="mx-auto max-w-md px-5 py-24">
        <Spinner label="Already signed in" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-md px-5 py-16">
      <h1 className="mb-2 text-3xl">
        {mode === 'sign-in' ? 'Sign in' : mode === 'forgot' ? 'Reset your password' : 'Choose a new password'}
      </h1>
      <p className="mb-8 text-[color:var(--color-ink-soft)]">
        {mode === 'sign-in'
          ? // One door, two people behind it: the client whose pets are on file,
            // and the sitter whose whole business is. Promising only a pet file
            // told a sitter on their own domain they were in the wrong place.
            'Pets, records and bookings for a client. The whole business for a sitter.'
          : mode === 'forgot'
            ? 'Enter your email and we will send a code.'
            : 'Enter the code from your email and pick a new password.'}
      </p>

      {notice && (
        <div className="mb-5">
          <Notice tone="ok">{notice}</Notice>
        </div>
      )}
      {error && (
        <div className="mb-5">
          <Notice tone="error">{error}</Notice>
        </div>
      )}

      <Card className="p-6">
        {mode === 'sign-in' && (
          <form onSubmit={onSignIn} className="space-y-5">
            <TextField label="Email" type="email" value={email} onChange={setEmail} autoComplete="email" required />
            <TextField
              label="Password"
              type="password"
              value={password}
              onChange={setPassword}
              autoComplete="current-password"
              required
            />
            <button type="submit" className="btn btn-primary w-full" disabled={busy}>
              {busy ? 'Signing in' : 'Sign in'}
            </button>
            <div className="flex justify-between pt-1 text-sm">
              <button type="button" className="btn btn-quiet" onClick={() => setMode('forgot')}>
                Forgot password
              </button>
              <Link to="/sign-up" className="btn btn-quiet">
                Create an account
              </Link>
            </div>
          </form>
        )}

        {mode === 'forgot' && (
          <form onSubmit={onForgot} className="space-y-5">
            <TextField label="Email" type="email" value={email} onChange={setEmail} autoComplete="email" required />
            <button type="submit" className="btn btn-primary w-full" disabled={busy}>
              {busy ? 'Sending' : 'Send reset code'}
            </button>
            <button type="button" className="btn btn-quiet w-full" onClick={() => setMode('sign-in')}>
              Back to sign in
            </button>
          </form>
        )}

        {mode === 'reset' && (
          <form onSubmit={onReset} className="space-y-5">
            <TextField label="Reset code" value={code} onChange={setCode} autoComplete="one-time-code" required />
            <TextField
              label="New password"
              type="password"
              value={password}
              onChange={setPassword}
              autoComplete="new-password"
              hint="At least 12 characters, with an uppercase letter, a lowercase letter and a number."
              required
            />
            <button type="submit" className="btn btn-primary w-full" disabled={busy}>
              {busy ? 'Saving' : 'Change password'}
            </button>
          </form>
        )}
      </Card>
    </div>
  );
}
